import { useEffect, useRef, useState } from 'react'
import { useAuthModal } from '@/context/AuthModalContext'
import { useDepositModal } from '@/context/DepositModalContext'
import { BETBY_BASENAME, BETBY_BRAND_ID, BETBY_SCRIPT_URL, type BTRendererInstance } from '@/lib/sportsbook-renderer'
import { useLazyRefreshSportsbookTokenQuery } from '@/store/api/sportsbookApi'

interface UseSportsbookRendererOptions {
	token: string | null
	sessionId: string | null
	enabled?: boolean
}

export function useSportsbookRenderer({ token, sessionId, enabled = true }: UseSportsbookRendererOptions) {
	const containerRef = useRef<HTMLDivElement>(null)
	const rendererRef = useRef<BTRendererInstance | null>(null)
	const { openLogin, openRegister } = useAuthModal()
	const { openDeposit } = useDepositModal()
	const [refreshSportsbookToken] = useLazyRefreshSportsbookTokenQuery()
	const [scriptLoaded, setScriptLoaded] = useState(false)

	useEffect(() => {
		if (typeof window === 'undefined' || window.BTRenderer) {
			setScriptLoaded(true)
			return
		}

		const existing = document.querySelector(`script[src="${BETBY_SCRIPT_URL}"]`)
		if (existing) {
			setScriptLoaded(true)
			return
		}

		const script = document.createElement('script')
		script.src = BETBY_SCRIPT_URL
		script.async = true
		script.onload = () => setScriptLoaded(true)
		script.onerror = () => setScriptLoaded(false)
		document.head.appendChild(script)
	}, [])

	useEffect(() => {
		if (!enabled || !scriptLoaded || !token || !sessionId || !containerRef.current) {
			return
		}

		const BTRenderer = window.BTRenderer
		if (!BTRenderer) {
			return
		}

		const target = containerRef.current

		const instance = new BTRenderer().initialize({
			brand_id: BETBY_BRAND_ID,
			token,
			themeName: 'default',
			lang: 'en',
			target,
			minFrameHeight: 700,
			betSlipOffsetTop: 0,
			betslipZIndex: 999,
			basename: BETBY_BASENAME,
			url: '/',
			onTokenExpired: async () => {
				const response = await refreshSportsbookToken(sessionId).unwrap()
				return response.data?.token?.trim() || null
			},
			onLogin: () => {
				openLogin()
			},
			onRegister: () => {
				openRegister()
			},
			onSessionRefresh: () => {
				openLogin()
			},
			onRecharge: () => {
				openDeposit()
			},
		})

		rendererRef.current = instance

		return () => {
			rendererRef.current?.kill?.()
			rendererRef.current = null
		}
	}, [enabled, openDeposit, openLogin, openRegister, refreshSportsbookToken, scriptLoaded, sessionId, token])

	return {
		containerRef,
		scriptLoaded,
	}
}
